import { Box } from '@mui/material';
import { DocumentType } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import { NorthEast } from '@mui/icons-material';
import {
    PaloConfirmationDialog,
    PaloDetailView,
    PaloHistory,
    PaloOverflowMenu,
    PaloStep,
    PaloTabs,
} from '@synergy/components-palo';
import { PoolDocumentTitle } from '@synergy/components-next';
import { PoolFormattedMessage, PoolSpinner } from '@synergy/components';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { SHOP_URL } from '@synergy/config';
import { SubPageHandle, getLocalizedString } from '@synergy/utils';
import urljoin from 'url-join';

import * as ActivityActions from '../../../../common/actions/activity-actions';
import * as AttributeActions from '../../../../common/actions/attributes-actions';
import * as ProductActions from '../store/product-actions';
import * as ProductDetailActions from '../product-detail/store/product-detail-actions';
import * as UnitActions from '../../../../common/actions/unit-actions';
import { ArticleNumber } from '../product-detail/components/article-number';
import { DetailStream } from '../../../activity-sidebar/details/detail-stream';
import { NoteType } from '../../../../common/models/enums/note-type';
import {
    PaloDirtyDot,
    PaloDirtyProvider,
    PaloDirtyScope,
    usePaloSaveOnLeave,
} from '../../../../common/components/palo/palo-dirty-context';
import { PaloRecordNotices } from '../../../../common/components/palo/palo-record-notices';
import { PaloSaveAction, paloDocumentPhase } from '../../../detail-header/palo-main-action';
import { ProductDetailAttributes } from '../product-detail/attributes/product-detail-attributes';
import { ProductDetailFiles } from '../product-detail/files/product-detail-files';
import { ProductDetailOverview } from '../product-detail/overview/product-detail-overview';
import { ProductDetailPrices } from '../product-detail/prices/product-detail-prices';
import { ProductDetailTabs } from '../product-detail/models/product-detail-tabs';
import { ProductDetailVariants } from '../product-detail/variants/product-detail-variants';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { localesSelector } from '../../../../common/selectors/locale-selectors';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { usePaloRecordKey } from '../../../contacts-and-organizations/components/use-palo-record-key';
import { useRedirectAfterNewSave } from '../../../contacts-and-organizations/components/use-redirect-after-new-save';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const PaloProductDetailInner = (): ReactElement => {
    const subPageRef = useRef<SubPageHandle>(null);
    const { id: productIdParam } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();
    const dispatch = useThunkDispatch();

    const locales = useAppSelector(localesSelector);
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const {
        productDetail,
        requesting,
        isValid: overviewIsValid,
    } = useAppSelector(state => state.product.detail.overview);
    const { isValid: pricesIsValid } = useAppSelector(state => state.product.detail.prices);
    const { isValid: attributesIsValid } = useAppSelector(state => state.product.detail.attributes);

    const currentTab = (location.pathname.split('/').pop() as ProductDetailTabs) ?? ProductDetailTabs.Overview;
    const isNew = productIdParam ? isNaN(+productIdParam) : false;
    const productName = isNew
        ? intl.formatMessage({ id: 'app.product.detail.productNew' })
        : getLocalizedString(locales, productDetail?.name);

    const [showDeleteWarning, setShowDeleteWarning] = useState(false);

    useRedirectAfterNewSave(ROUTES.products.product.detail, productDetail?.id);
    usePaloSaveOnLeave(subPageRef.current?.submit);

    useEffect(() => {
        dispatch(UnitActions.fetchAllUnits());
        dispatch(AttributeActions.fetchAttributes(new URLSearchParams({ limit: '10000' })));
        dispatch(ActivityActions.setDocumentType(DocumentType.Product));

        return () => {
            dispatch(ProductDetailActions.resetProductDetails());
            dispatch(ActivityActions.resetActivities());
        };
    }, [dispatch]);

    useEffect(() => {
        if (productDetail?.id) {
            dispatch(ActivityActions.fetchActivities(DocumentType.Product, productDetail.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productDetail?.id]);

    const handleSave = async (): Promise<void> => {
        if (subPageRef.current) {
            await subPageRef.current.validate();
            await subPageRef.current.submit();
        }
        await dispatch(ProductDetailActions.saveProductDetails());
        if (productDetail?.id) {
            dispatch(ActivityActions.fetchActivities(DocumentType.Product, productDetail.id));
        }
    };

    const handleActivateDeactivate = (): void => {
        if (!productDetail) {
            return;
        }
        dispatch(
            productDetail.isActive ? ProductDetailActions.deactivateProduct() : ProductDetailActions.activateProduct()
        );
    };

    const handleDelete = (): void => {
        setShowDeleteWarning(false);
        dispatch(ProductDetailActions.deleteProduct());
    };

    const handleDuplicate = async (): Promise<void> => {
        if (!productDetail?.id) {
            return;
        }
        const result = await dispatch(ProductActions.duplicateProduct(productDetail.id));
        if (result) {
            dispatch(ProductDetailActions.resetProductDetails());
            navigate(ROUTES.products.product.detail.replace(':id', result.id.toString()));
        }
    };

    const detailBase = ROUTES.products.product.detail.replace(':id', String(productDetail?.id ?? productIdParam));
    const activeTab = (Object.values(ProductDetailTabs) as string[]).includes(currentTab)
        ? currentTab
        : ProductDetailTabs.Overview;

    const tabLabel = (tab: string, label: ReactElement, valid = true): ReactElement => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {label}
            {!valid && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'error.main' }} />}
            <PaloDirtyDot scope={tab} />
        </Box>
    );

    const saveFirst = <FormattedMessage id="app.product.detail.saveFirst" />;

    const menu = (
        <PaloOverflowMenu
            actions={[
                {
                    label: (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <FormattedMessage id="app.product.detail.showInShop" />
                            <NorthEast sx={{ fontSize: 14 }} />
                        </Box>
                    ),
                    // The shop only shows an active article, so a deactivated
                    // one would land on an empty page.
                    disabled: isNew || !productDetail?.isActive,
                    reason: isNew ? saveFirst : <FormattedMessage id="app.product.detail.inactiveInShop" />,
                    separated: true,
                    onClick: () =>
                        window.open(urljoin(SHOP_URL, 'product', String(productDetail?.id)), '_blank', 'noopener'),
                },
                {
                    label: (
                        <FormattedMessage
                            id={productDetail?.isActive ? 'app.common.deactivate' : 'app.common.activate'}
                        />
                    ),
                    disabled: isNew,
                    reason: saveFirst,
                    onClick: handleActivateDeactivate,
                },
                {
                    label: <FormattedMessage id="app.common.duplicate" />,
                    disabled: isNew,
                    reason: saveFirst,
                    onClick: handleDuplicate,
                },
                {
                    label: <FormattedMessage id="app.common.delete" />,
                    disabled: isNew,
                    reason: saveFirst,
                    danger: true,
                    onClick: () => setShowDeleteWarning(true),
                },
            ]}
        />
    );

    const actions = (
        <PaloSaveAction
            phase={paloDocumentPhase(undefined, { saved: !isNew })}
            comparesToServer={true}
            onSave={handleSave}
        />
    );

    const tabSteps: PaloStep[] = [
        [ProductDetailTabs.Overview, 'details', overviewIsValid, false],
        [ProductDetailTabs.Prices, 'prices', pricesIsValid, true],
        [ProductDetailTabs.Files, 'files', true, true],
        [ProductDetailTabs.Attributes, 'attributes', attributesIsValid, true],
        [ProductDetailTabs.Variants, 'variants', true, true],
    ].map(([tab, key, valid, needsId]) => ({
        label: tabLabel(tab as string, <FormattedMessage id={`app.product.detail.tab.${key}`} />, valid as boolean),
        state: activeTab === tab ? 'now' : 'todo',
        disabled: (needsId as boolean) && isNew,
        onClick: () => navigate(`${detailBase}/${tab}`),
    }));

    const tabs = <PaloTabs steps={tabSteps} />;

    return (
        <PoolSpinner enabled={requesting}>
            <PoolDocumentTitle titleTranslationId={'app.navigation.product'} name={productName} />
            <PaloDetailView
                themeContent={false}
                breadcrumbs={[{ label: 'Artikel', onClick: () => navigate(ROUTES.products.product.base) }]}
                number={productDetail ? <ArticleNumber productDetail={productDetail} /> : undefined}
                title={productName}
                menu={menu}
                actions={actions}
                alert={<PaloRecordNotices noteType={NoteType.Products} entityId={productDetail?.id} />}
                tabs={tabs}
                history={
                    <PaloHistory>
                        <DetailStream documentType={DocumentType.Product} documentId={productDetail?.id} />
                    </PaloHistory>
                }
            >
                <Routes>
                    <Route
                        path={ProductDetailTabs.Overview}
                        element={
                            <PaloDirtyScope name={ProductDetailTabs.Overview}>
                                <ProductDetailOverview ref={subPageRef} selectedLanguage={selectedLanguage} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route path={ProductDetailTabs.Files} element={<ProductDetailFiles />} />
                    <Route
                        path={ProductDetailTabs.Attributes}
                        element={
                            <PaloDirtyScope name={ProductDetailTabs.Attributes}>
                                <ProductDetailAttributes ref={subPageRef} selectedLanguage={selectedLanguage} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route
                        path={ProductDetailTabs.Prices}
                        element={
                            <PaloDirtyScope name={ProductDetailTabs.Prices}>
                                <ProductDetailPrices ref={subPageRef} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route
                        path={ProductDetailTabs.Variants}
                        element={<ProductDetailVariants selectedLanguage={selectedLanguage} />}
                    />
                    <Route
                        path="*"
                        element={<Navigate to={{ pathname: ProductDetailTabs.Overview }} replace={true} />}
                    />
                </Routes>
            </PaloDetailView>

            <PaloConfirmationDialog
                isVisible={showDeleteWarning}
                title={<FormattedMessage id="app.product.detail.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                onCancel={() => setShowDeleteWarning(false)}
                onConfirm={handleDelete}
            >
                <PoolFormattedMessage id="app.product.detail.deleteWarning" values={{ name: productName }} />
            </PaloConfirmationDialog>
        </PoolSpinner>
    );
};

export const PaloProductDetail = (): ReactElement => {
    const { id } = useParams<{ id: string }>();
    const recordKey = usePaloRecordKey(id);
    // A fresh document from the server is the one moment at which every part
    // of the mask, mounted or not, is provably saved.
    const loadedDocument = useAppSelector(state => state.product.detail.overview.baseline);

    return (
        <PaloDirtyProvider
            key={recordKey}
            basePath={ROUTES.products.product.detail.replace(':id', String(id))}
            loadedDocument={loadedDocument}
        >
            <PaloProductDetailInner />
        </PaloDirtyProvider>
    );
};
